import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0 } from "../../lib/format";
import { HBars } from "../../lib/charts";
import { Notice, Stat } from "../../lib/ui";

/*
 * Expiry dashboard – port of expiry_dashboard.php (API: /api/stock/expiry-dashboard)
 * Products with an expiry date and more than 1 in stock, grouped: expired, critical (≤ 7 days), warning (≤ 30),
 * watch (≤ 90), safe. Value at risk = retail value of expired + critical + warning. Edit an expiry date in place.
 */

const B = { expired: ["Expired", "danger"], critical: ["Critical ≤ 7 days", "danger"], warning: ["Warning ≤ 30 days", "warn"], watch: ["Watch ≤ 90 days", "brand"], safe: ["Safe", "up"] };
const PILL = { expired: "err", critical: "err", warning: "warn", watch: "brand", safe: "ok" };

export default function ExpiryDashboard() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("days");
  const [edit, setEdit] = useState({});
  const [notice, setNotice] = useState(null);
  const load = useCallback(() => api.get("/api/stock/expiry-dashboard").then(setD).catch((e) => setNotice({ tone: "err", text: e.message })), [api]);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => (d?.rows || []).filter((r) => (filter === "all" || r.bucket === filter)
    && (!q || `${r.name} ${r.manufacturer || ""}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (sort === "days" ? a.days - b.days : sort === "value" ? b.value_retail - a.value_retail : sort === "qty" ? b.qty - a.qty : a.name.localeCompare(b.name))), [d, filter, q, sort]);
  const save = async (r) => {
    try { await api.post(`/api/products/${r.id}/expiry`, { expiry_date: edit[r.id] }); setNotice({ tone: "ok", text: `Expiry date updated for ${r.name}.` }); setEdit((e) => { const { [r.id]: _, ...x } = e; return x; }); load(); }
    catch (e) { setNotice({ tone: "err", text: e.message }); }
  };
  const exportCsv = () => {
    const out = [["Status", "Product", "Manufacturer", "Type", "Stock", "Retail value", "Expiry", "Days"], ...rows.map((r) => [B[r.bucket][0], r.name, r.manufacturer, r.type, r.qty, r.value_retail, r.expiry, r.days])];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([out.map((x) => x.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" }));
    a.download = "expiry_dashboard.csv"; a.click();
  };
  const daysLabel = (n) => (n < 0 ? `${-n} day${n === -1 ? "" : "s"} ago` : n === 0 ? "today" : `in ${n} day${n === 1 ? "" : "s"}`);
  return (
    <main className="pg" style={{ maxWidth: "90rem" }}>
      <PageHeader slug="expiry_dashboard" actions={<><button type="button" className="btn" onClick={exportCsv} disabled={!rows.length}><i className="fas fa-file-csv" /> Export CSV</button>
        <button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print</button></>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      {d && (
        <>
          <div className="ui-stats">
            {Object.entries(B).map(([k, [l, tone]]) => (
              <button key={k} type="button" className={`ui-stat io-card ${filter === k ? "on" : ""}`} aria-pressed={filter === k} onClick={() => setFilter(filter === k ? "all" : k)}>
                <span className={`ui-stat-ic ${tone}`}><i className="fas fa-hourglass-half" /></span><div><div className="ui-stat-label">{l}</div><div className="ui-stat-value mono">{d.kpi[k]}</div></div></button>))}
            <Stat icon="fa-sack-dollar" tone="danger" label={`Value at risk · cost ${n0(d.cost_at_risk)}`} value={n0(d.value_at_risk)} unit="TZS" />
          </div>
          {d.by_manufacturer.length > 0 && (
            <div className="grid2">
              <section className="panel card-pad"><h3>At risk by manufacturer</h3><HBars rows={d.by_manufacturer.map((m) => ({ label: m.name, values: [m.value] }))} names={["Retail value"]} /></section>
              <section className="panel card-pad"><h3>At risk by product type</h3><HBars rows={d.by_type.map((m) => ({ label: m.name, values: [m.value] }))} names={["Retail value"]} /></section>
            </div>
          )}
        </>
      )}
      <section className="panel">
        <div className="toolbar">
          <div className="seg-filter" role="group" aria-label="Status">{["all", ...Object.keys(B)].map((k) => <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{k === "all" ? "All" : B[k][0].split(" ")[0]}</button>)}</div>
          <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or manufacturer" aria-label="Filter" /></div></div>
          <div className="field"><select aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="days">Soonest expiring</option><option value="value">Highest value</option><option value="qty">Highest stock</option><option value="name">Name A–Z</option></select></div>
        </div>
        <div className="tbl-wrap"><table className="tbl">
          <thead><tr><th>Status</th><th>Product</th><th>Manufacturer</th><th className="num">Stock</th><th className="num">Retail value</th><th>Expiry</th><th /></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id}>
              <td><span className={`pill-tag ${PILL[r.bucket]}`}>{B[r.bucket][0].split(" ")[0]}</span></td>
              <td><b style={{ color: "var(--ink)" }}>{r.name}</b>{r.type && <div style={{ fontSize: ".72rem", color: "var(--muted)" }}>{r.type}</div>}</td>
              <td>{r.manufacturer || "–"}</td><td className="num mono">{n0(r.qty)} <small style={{ color: "var(--muted)" }}>{r.unit}</small></td><td className="num mono">{n0(r.value_retail)}</td>
              <td><input type="date" className="cell" style={{ maxWidth: "10rem", fontFamily: "inherit" }} aria-label={`Expiry date of ${r.name}`} value={edit[r.id] ?? r.expiry}
                onChange={(e) => setEdit({ ...edit, [r.id]: e.target.value })} /><div style={{ fontSize: ".72rem", color: r.days < 0 ? "var(--danger)" : "var(--muted)" }}>{daysLabel(r.days)}</div></td>
              <td>{edit[r.id] !== undefined && edit[r.id] !== r.expiry && <button type="button" className="btn sm primary" onClick={() => save(r)}>Save</button>}</td>
            </tr>))}</tbody>
        </table>{d && rows.length === 0 && <div className="empty"><i className="fas fa-hourglass" />No products in this view.</div>}</div>
      </section>
    </main>
  );
}
